/**
 * The registrar course record (`/admin/courses/{courseId}`), the one address of
 * a section in the registrar shell. The Desk is the bare path; the record, roster,
 * attendance, coursework, and grades are `?view=` on it, each the full width of
 * the page. The view is read from `?view=` and nothing else, so a link, a
 * reload, and the browser history always agree about what is on screen.
 *
 * Archived and closed-term sections stay readable here. Each view keeps the
 * controls the server would refuse on them disabled or unoffered.
 */
import { useState } from "react";
import { CourseDesk } from "@/components/courses/CourseDesk";
import { useAuth } from "@/contexts/AuthContext";
import type { Course } from "@/lib/api/courses";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useParams } from "react-router";

import { RegistrarCourseAttendance } from "@/components/registrar/course/RegistrarCourseAttendance";
import { RegistrarCourseCoursework } from "@/components/registrar/course/RegistrarCourseCoursework";
import { RegistrarCourseGrades } from "@/components/registrar/course/RegistrarCourseGrades";
import { RegistrarCourseHeader } from "@/components/registrar/course/RegistrarCourseHeader";
import { RegistrarCourseRecord, RegistrarCourseRecordRead } from "@/components/registrar/course/RegistrarCourseRecord";
import { RegistrarCourseRoster } from "@/components/registrar/course/RegistrarCourseRoster";
import { RegistrarCourseViews } from "@/components/registrar/course/RegistrarCourseViews";
import { WorkspaceState } from "@/components/records/AcademicWorkspace";
import { TileHeadingLevel } from "@/components/ui/tile";
import { Bento, PageRefusal } from "@/components/ui/box";
import { useTabDraft } from "@/components/ui/box-draft";
import type { TaskBox } from "@/components/ui/box.schema";
import { useSectionTaskResults, type SectionPending } from "@/components/registrar/course/section-task-result";
import { ApiError } from "@/lib/api/client";
import { coursesApi } from "@/lib/api/courses";
import {
  PROTECTED_ROUTES,
  isRegistrarCourseView,
  registrarCoursePath,
  type RegistrarCourseView,
} from "@/navigation/routes";

/**
 * The view an arrival names, and the canonical URL when it is not already the
 * one on screen. One `view` that names a known view other than Desk is
 * kept; anything else is Desk, whose address is the bare path. An
 * enrollment notice can open inline on the record or its full Roster view.
 * Every other parameter and the hash survive.
 */
function resolveView(
  courseId: string,
  search: string,
  hash: string,
): { view: RegistrarCourseView; replacement: string | null } {
  const params = new URLSearchParams(search);
  const named = params.getAll("view");
  const requested = named.length === 1 && isRegistrarCourseView(named[0]) ? named[0] : params.has("enrollment-notice") ? "record" : "desk";
  const view = requested;
  const canonical = view !== "desk" && named.length === 1 && named[0] === view;
  if (canonical || (view === "desk" && named.length === 0)) return { view, replacement: null };

  params.delete("view");
  const path = registrarCoursePath(courseId, view);
  const rest = params.toString();
  return {
    view,
    replacement: `${path}${rest ? `${path.includes("?") ? "&" : "?"}${rest}` : ""}${hash}`,
  };
}

/** A stored draft is not authority for a different command or subject. */
function isPendingDiscard(value: unknown, courseId: string): value is SectionPending {
  if (!value || typeof value !== "object") return false;
  const command = value as Partial<SectionPending>;
  return command.action === "course.discard" && command.course === courseId
    && typeof command.key === "string" && command.key.length >= 8 && command.key.length <= 200 && command.key.trim() === command.key
    && Array.isArray(command.effects) && command.effects.every(line => typeof line === "string")
    && Array.isArray(command.remains) && command.remains.every(line => typeof line === "string");
}

export function RegistrarCoursePage() {
  const { courseId = "" } = useParams();
  const { me, role } = useAuth();
  const owner = `${me?.tenant_id}/${me?.user_id}`;
  const [discarded, setDiscarded] = useState<{ record: Course; owner: string } | null>(null);
  const discardDraft = useTabDraft<{ code: string; reason: string }, SectionPending>({ box: "course-publication", record: `${courseId}/discard`, version: null, initial: { code: "", reason: "" } });
  const [recovery, setRecovery] = useState<{ owner: string; course: string; command: SectionPending } | null>(null);
  const pendingDiscard = role === "admin" && me?.tenant_id && me.user_id && discardDraft.settled && isPendingDiscard(discardDraft.pending, courseId) ? discardDraft.pending : null;
  // Keep only the sent command through Check clearing its pending draft before its result arrives.
  if (pendingDiscard && (recovery?.owner !== owner || recovery.course !== courseId || recovery.command.key !== pendingDiscard.key))
    setRecovery({ owner, course: courseId, command: pendingDiscard });
  const recoveryCommand = pendingDiscard ?? (discardDraft.pending === null && recovery?.owner === owner && recovery.course === courseId ? recovery.command : null);
  const recoveryResults = useSectionTaskResults();
  const recoveryResult = recoveryResults.result(`discard-${courseId}`, discardDraft);
  const { search, hash } = useLocation();
  const { view, replacement } = resolveView(courseId, search, hash);

  const course = useQuery({
    queryKey: ["course", courseId],
    queryFn: () => coursesApi.get(courseId),
    enabled: courseId !== "" && replacement === null,
  });

  if (!courseId) return <Navigate replace to={PROTECTED_ROUTES.admin.courses} />;
  if (replacement !== null) return <Navigate replace to={replacement} />;

  const ownDiscard = discarded?.owner === owner && discarded.record.id === courseId;
  const record = course.data ?? (ownDiscard ? discarded.record : undefined);
  const listHref = record?.status === "archived"
    ? `${PROTECTED_ROUTES.admin.courses}?status=archived`
    : PROTECTED_ROUTES.admin.courses;
  const missing = course.error instanceof ApiError && course.error.status === 404;
  const refused = course.error instanceof ApiError && [401, 403, 404].includes(course.error.status) && !(missing && ownDiscard);

  if (missing && recoveryCommand && !ownDiscard && role === "admin") {
    const recoveryBox: TaskBox<SectionPending> = {
      id: `discard-${courseId}`, kind: "task", label: "Discard test section", job: "Check the original Discard already sent from this tab.", owner: { system: "imam" },
      read: { status: "ready", value: recoveryCommand, readAt: new Date().toISOString() }, fold: "never",
      state: () => ({ class: recoveryResult.state === "confirmed" ? "done" : "quiet", word: recoveryResult.state === "confirmed" ? "Discarded" : "Not confirmed" }),
      act: recoveryResult.state === "unknown" ? { does: "check", verb: "Check" } : undefined,
      detail: () => "Read the original Discard outcome.", draft: discardDraft.kept,
      parts: [{ id: "discard-command", label: "Original Discard", owner: { system: "imam" }, state: { class: recoveryResult.state === "confirmed" ? "done" : "quiet", word: recoveryResult.state === "confirmed" ? "Discarded" : "Not confirmed" }, markedByBox: true, fold: "never", draft: "browser", keep: discardDraft, render: () => null }], result: recoveryResult,
      exits: [{ label: "Courses", to: PROTECTED_ROUTES.admin.courses }],
      // The recovery surface never resends Discard. Only the existing result's Check can run.
      commit: { verb: "Check", cap: "Check the original Discard", effects: () => [], blockers: () => [{ kind: "unchanged", part: "discard-command" }], run: async () => recoveryResult },
    };
    return <div className="app-page app-page--roomy"><RegistrarCourseHeader listHref={PROTECTED_ROUTES.admin.courses} /><TileHeadingLevel as="h3"><Bento id={`course-record-${courseId}`} journey="registrar-course-record" boxes={[recoveryBox]} /></TileHeadingLevel></div>;
  }

  if (refused) return <div className="app-page app-page--roomy"><RegistrarCourseHeader listHref={listHref} /><PageRefusal id={`course-record-${courseId}`} journey="registrar-course-record" refusal={{ reason: "unavailable", sentence: "You don't have access to this section.", back: { label: "Courses", to: PROTECTED_ROUTES.admin.courses } }} /></div>;

  if (view === "desk" && record?.status === "archived") {
    const params = new URLSearchParams(search);
    params.set("view", "record");
    return <Navigate replace to={`${registrarCoursePath(courseId)}?${params}${hash}`} />;
  }

  return (
    <div className="app-page app-page--roomy">
      <RegistrarCourseHeader course={view === "record" && refused ? undefined : record} listHref={listHref} />

      {view === "record" && refused ? <PageRefusal id={`course-record-${courseId}`} journey="registrar-course-record" refusal={{ reason: "unavailable", sentence: "You don't have access to this section.", back: { label: "Courses", to: PROTECTED_ROUTES.admin.courses } }} /> : view === "record" && !record ? <RegistrarCourseRecordRead recordId={courseId} read={course.isPending ? { status: "loading" } : { status: "failed", sentence: "This section could not be read. Try again.", retry: () => void course.refetch() }} /> : null}
      {view !== "record" && course.isPending && <WorkspaceState kind="loading" description="Loading section record" />}
      {view !== "record" && missing && (
        <WorkspaceState
          kind="empty"
          title="Section not found"
          description="No section in this institution has this address. It may have been discarded."
        />
      )}
      {view !== "record" && course.isError && !missing && !record && (
        <WorkspaceState
          kind="error"
          title="Section record unavailable"
          description="The selected section could not be loaded."
          onRetry={() => void course.refetch()}
        />
      )}

      {record && !(view === "record" && refused) && (
        <>
          <RegistrarCourseViews courseId={record.id} active={view} />
          {/* Every tile heading in a view nests under the course's h2. */}
          <TileHeadingLevel as="h3">
            {view === "desk" && <CourseDesk courseId={record.id} perspective="administration" backHref={listHref} />}
            {view === "record" && <RegistrarCourseRecord course={record} listHref={listHref} discarded={ownDiscard} onDiscarded={record => setDiscarded({ record, owner })} read={course.isError && !ownDiscard ? { status: "stale", last: record, readAt: new Date(course.dataUpdatedAt).toISOString(), sentence: "Could not refresh. A later change is not shown.", refresh: () => void course.refetch() } : { status: "ready", value: record, readAt: new Date(course.dataUpdatedAt).toISOString() }} />}
            {view === "roster" && <RegistrarCourseRoster course={record} />}
            {view === "attendance" && <RegistrarCourseAttendance courseId={record.id} />}
            {view === "coursework" && <RegistrarCourseCoursework courseId={record.id} />}
            {view === "grades" && <RegistrarCourseGrades courseId={record.id} />}
          </TileHeadingLevel>
        </>
      )}
    </div>
  );
}
